import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import OpacityBg from '../components/OpacityBg'
import { hasText } from '../utils/helpers'
import ui from '.'

function ModalDialog({
  title,
  opened,
  onClose,
  children,
  noScroll,
  hideCloseButton,
  footer,
  header,
  width,
  disableOutsideClick,
  maxWidth,
  overflow = true,
  withScroll,
}) {
  const [hasScroll, setScroll] = useState(false)
  const [hasScrollToBottom, setScrollToBottom] = useState(true)
  const scrollRef = useRef(null)

  useEffect(() => {
    if (scrollRef?.current?.scrollHeight > scrollRef?.current?.offsetHeight) {
      setScrollToBottom(false)
    }
  }, [children])

  const handleScroll = useCallback(
    (e) => {
      const scrollTop = e?.nativeEvent?.srcElement?.scrollTop
      if (isNaN(scrollTop)) return
      if (scrollTop > 0 && !hasScroll) {
        setScroll(true)
      } else if (scrollTop === 0 && hasScroll) {
        setScroll(false)
      }
      const scrollHeight = e?.nativeEvent?.srcElement?.scrollHeight
      if (isNaN(scrollHeight)) return
      const offsetHeight = e?.nativeEvent?.srcElement?.offsetHeight
      if (isNaN(offsetHeight)) return
      const delta = scrollHeight - (offsetHeight + scrollTop)
      if (delta > 0 && hasScrollToBottom) {
        setScrollToBottom(false)
      } else if (delta <= 0 && !hasScrollToBottom) {
        setScrollToBottom(true)
      }
    },
    [hasScroll, hasScrollToBottom]
  )

  const handleOutsideClick = useCallback(() => {
    if (disableOutsideClick) return

    onClose()
  }, [onClose, disableOutsideClick])

  if (!opened) {
    return null
  }

  return (
    <div>
      <OpacityBg useMaxZIndex onClick={handleOutsideClick}>
        <div
          className="z-9999 bg-white mx-auto shadow rounded-lg w-11/12 sm:w-10/12 md:w-8/12 lg:w-6/12 flex flex-col relative"
          onClick={(e) => e.stopPropagation()}
          style={{
            maxHeight: '94vh',
            minHeight: noScroll ? '94vh' : undefined,
            width,
            maxWidth,
          }}
        >
          {!hideCloseButton && (
            <ui.ButtonIconClose
              className="absolute top-0 right-0 mt-3 mr-3"
              onClick={onClose}
            />
          )}
          {hasText(title) && (
            <h2 className="text-2xl font-bold mb-1 px-4 sm:px-6 pt-5 sm:pt-6">
              {title}
            </h2>
          )}
          {header && header}
          {noScroll ? (
            <div className="px-4 flex flex-1 min-h-0">{children}</div>
          ) : (
            <div
              className={`px-4 sm:px-6${hasScroll ? ' border-t' : ''}${
                footer ? '' : ' pb-5 sm:pb-6'
              }${!hasScrollToBottom && !!footer ? ' border-b' : ''}${
                overflow ? ' overflow-auto' : ''
              }${withScroll ? ' overflow-y-scroll' : ''}`}
              onScroll={handleScroll}
              ref={scrollRef}
            >
              {children}
            </div>
          )}
          {footer && footer}
        </div>
      </OpacityBg>
    </div>
  )
}

ModalDialog.defaultProps = {
  onClose: () => {},
}

ModalDialog.propTypes = {
  title: PropTypes.string,
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ModalDialog
